/*
 * EBIKO Draft Order PDF
 * --------------------------------------------------------------------------
 * Cart-page button that turns the live cart into a branded "DRAFT ORDER" PDF
 * the customer can download, print, and forward to a doctor for sign-off.
 * Styled to match EBIKO's customer quote template.
 *
 * 100% client-side. No backend, no email sent from the site.
 *  - Live cart from /cart.js; bill-to/ship-to from the logged-in customer
 *  - compare_at prices from Liquid (#ebk-compare-at); named discounts from /cart.js
 *  - Real EBIKO logo + product thumbnails; original price + savings shown
 *
 * The pure builder `EbikoDraftOrder.build(doc, data)` is environment-agnostic
 * so the same layout code is reused by the Node preview harness.
 * --------------------------------------------------------------------------
 */
(function (root) {
  'use strict';

  // ---- Brand palette -------------------------------------------------------
  var NAVY  = [31, 78, 121];     // #1F4E79  headings, table header, boxes, footer
  var DARK  = [51, 51, 51];
  var MUTED = [120, 120, 120];
  var ZEBRA = [240, 245, 249];   // alt rows
  var PANEL = [246, 249, 251];   // totals panel bg
  var LINE  = [214, 220, 226];
  var SAGE  = [150, 190, 178];   // header divider + acceptance border
  var SAGEBG = [224, 236, 232];  // acceptance box fill
  var SAVE  = [192, 0, 0];       // savings red
  var WHITE = [255, 255, 255];

  var SHOP = {
    name: 'EBIKO Dental',
    addr: '361 Steelcase Rd W, Unit 8, Markham, ON  L3R 3V8',
    contact: '437-878-4588   |   ebikodental@gmail.com   |   EBIKO.ca',
    phone: '437-878-4588',
    email: 'ebikodental@gmail.com',
    web: 'EBIKO.ca'
  };

  var TERMS = [
    'This draft order reflects current online pricing and is subject to product availability.',
    'Prices are in Canadian Dollars (CAD). Final taxes and shipping are calculated at checkout.',
    'Free shipping on orders $99+ within the GTA, $199+ within Ontario, $299+ across Canada.',
    'Sale and promotional pricing (incl. ODA specials) is subject to change and may expire.',
    'Returns accepted within 30 days of delivery in original unopened packaging. Custom orders are non-refundable.',
    'Backorders are fulfilled as stock becomes available; you will be notified of expected delivery.',
    'This is a draft order for review/approval — it is not an invoice and does not place an order.'
  ];
  var ACCEPT_TEXT = 'I approve this order and authorize it to be placed with EBIKO Dental as described above.';

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function billLines(c) {
    if (!c) return [];
    var out = [];
    var nm = (c.name || ((c.first_name || '') + ' ' + (c.last_name || ''))).trim();
    if (nm) out.push({ t: nm, b: true });
    if (c.company) out.push({ t: c.company });
    var street = c.address1 || '';
    if (street) out.push({ t: street });
    if (c.address2) out.push({ t: c.address2 });
    var city = [c.city, c.province, c.zip].filter(Boolean).join(', ');
    if (city) out.push({ t: city });
    if (c.phone) out.push({ t: 'Tel: ' + c.phone });
    if (c.email) out.push({ t: c.email, m: true });
    return out;
  }
  function shipLines(c) {
    if (!c) return [];
    var out = [];
    if (c.company) out.push({ t: c.company, b: true });
    var nm = (c.name || ((c.first_name || '') + ' ' + (c.last_name || ''))).trim();
    if (nm) out.push({ t: nm });
    if (c.address1) out.push({ t: c.address1 });
    if (c.address2) out.push({ t: c.address2 });
    var city = [c.city, c.province, c.zip].filter(Boolean).join(', ');
    if (city) out.push({ t: city });
    return out;
  }

  function lineCalc(it, compareAt) {
    var qty = it.quantity || 1;
    var unitFinal = (it.final_price != null) ? it.final_price : it.price;
    var lineFinal = (it.final_line_price != null) ? it.final_line_price : (unitFinal * qty);
    var unitOrig = (it.original_price != null) ? it.original_price : unitFinal;
    var lineOrig = (it.original_line_price != null) ? it.original_line_price : lineFinal;
    var titles = (it.discounts || []).map(function (d) { return d.title; }).filter(Boolean);
    var origUnit = null, origLine = lineFinal;
    if (unitOrig > unitFinal) { origUnit = unitOrig; origLine = lineOrig; }
    else if (compareAt && compareAt > unitFinal) { origUnit = compareAt; origLine = compareAt * qty; }
    var savings = origLine - lineFinal;
    var pct = (origLine > 0 && savings > 0) ? Math.round(savings / origLine * 100) : 0;
    return { qty: qty, unitFinal: unitFinal, lineFinal: lineFinal, origUnit: origUnit, origLine: origLine, savings: savings, pct: pct, titles: titles };
  }

  // ---- PURE BUILDER --------------------------------------------------------
  function buildDraftOrder(doc, data) {
    data = data || {};
    var cart = data.cart || {};
    var items = data.items || cart.items || [];
    var customer = data.customer || null;
    var compareMap = data.compareMap || {};
    var logo = data.logo || null;
    var dateStr = data.dateStr || '';
    var docNumber = data.docNumber || '';

    var PW = doc.internal.pageSize.getWidth();
    var PH = doc.internal.pageSize.getHeight();
    var M = 36;
    var FOOT = 30;             // footer bar height reserve

    // ===== Header =====
    if (logo && logo.dataUrl) {
      var lh = 38, lw = lh * (logo.ratio || 1.78);
      try { doc.addImage(logo.dataUrl, 'PNG', M, M, lw, lh); } catch (e) { logo = null; }
    }
    if (!logo || !logo.dataUrl) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(24); doc.setTextColor.apply(doc, NAVY);
      doc.text('EBIKO', M, M + 26);
    }
    // Title box (top-right)
    var boxW = 200, boxH = 58, boxX = PW - M - boxW, boxY = M - 8;
    doc.setFillColor.apply(doc, NAVY);
    doc.roundedRect(boxX, boxY, boxW, boxH, 8, 8, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.setTextColor.apply(doc, WHITE);
    doc.text('DRAFT ORDER', boxX + boxW / 2, boxY + 30, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor.apply(doc, WHITE);
    doc.text(SHOP.web, boxX + boxW / 2, boxY + 46, { align: 'center' });

    // Address (left, under logo)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor.apply(doc, MUTED);
    doc.text(SHOP.addr, M, M + 54);
    doc.text(SHOP.contact, M, M + 66);

    // Meta (right, under box)
    doc.setFontSize(9); doc.setTextColor.apply(doc, MUTED);
    var metaY = boxY + boxH + 16;
    function meta(label, value, yy, valColor) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor.apply(doc, valColor || NAVY);
      var vw = doc.getTextWidth(value);
      doc.text(value, PW - M, yy, { align: 'right' });
      doc.setFont('helvetica', 'normal'); doc.setTextColor.apply(doc, MUTED);
      doc.text(label, PW - M - vw - 6, yy, { align: 'right' });
    }
    if (docNumber) meta('No:', docNumber, metaY, NAVY);
    if (dateStr) meta('Date:', dateStr, metaY + 14, DARK);

    var y = Math.max(M + 66, metaY + 14) + 14;
    doc.setDrawColor.apply(doc, SAGE); doc.setLineWidth(2); doc.line(M, y, PW - M, y);
    y += 20;

    // ===== Bill to / Ship to =====
    var colR = M + (PW - 2 * M) / 2 + 10;
    function block(x, heading, lines) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor.apply(doc, NAVY);
      doc.text(heading, x, y);
      var yy = y + 14;
      if (!lines.length) {
        doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor.apply(doc, MUTED);
        doc.text('Sign in to auto-fill your clinic details.', x, yy); return yy;
      }
      lines.forEach(function (ln) {
        doc.setFont('helvetica', ln.b ? 'bold' : 'normal'); doc.setFontSize(ln.b ? 10 : 9);
        doc.setTextColor.apply(doc, ln.m ? MUTED : DARK);
        doc.text(String(ln.t), x, yy); yy += 12.5;
      });
      return yy;
    }
    var by = block(M, 'BILL TO', billLines(customer));
    var sy = block(colR, 'SHIP TO', shipLines(customer));
    y = Math.max(by, sy) + 14;

    // ===== Items table =====
    var calc = items.map(function (it) {
      var ca = compareMap[String(it.variant_id != null ? it.variant_id : it.id)] || 0;
      return lineCalc(it, ca);
    });
    function rowBg(i) { return (i % 2 === 1) ? ZEBRA : WHITE; }

    var body = items.map(function (it, i) {
      var c = calc[i];
      var name = it.product_title || it.title || '';
      var variant = (it.variant_title && it.variant_title !== 'Default Title') ? it.variant_title : '';
      var sku = it.sku || '';
      var prod = name + (variant ? ('\n' + variant) : '');
      if (c.titles.length) prod += '\n' + c.titles.join('; ');
      var unitText = c.origUnit ? '' : money(c.unitFinal);   // drawn manually when discounted
      var disc = c.pct > 0 ? ('-' + c.pct + '%') : '—';
      return ['', prod, sku, String(c.qty), unitText, disc, money(c.lineFinal)];
    });

    doc.autoTable({
      head: [['', 'Product', 'SKU', 'Qty', 'Unit Price', 'Disc%', 'Line Total']],
      body: body,
      startY: y,
      margin: { left: M, right: M, bottom: FOOT + 14, top: M },
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: 8.8, cellPadding: 6, textColor: DARK, lineColor: [230, 233, 236], lineWidth: 0.5, valign: 'middle', overflow: 'linebreak' },
      headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 8.5, halign: 'left' },
      alternateRowStyles: { fillColor: ZEBRA },
      bodyStyles: { minCellHeight: 42 },
      columnStyles: {
        0: { cellWidth: 42, halign: 'center' },                    // thumbnail
        1: { cellWidth: 'auto' },                                  // product
        2: { cellWidth: 60 },                                      // SKU
        3: { cellWidth: 30, halign: 'center', fontStyle: 'bold' }, // qty
        4: { cellWidth: 64, halign: 'right' },                     // unit price
        5: { cellWidth: 42, halign: 'center' },                    // disc%
        6: { cellWidth: 66, halign: 'right', fontStyle: 'bold' }   // line total
      },
      didParseCell: function (d) {
        if (d.section === 'body' && d.column.index === 1) {
          // hide native product text (we overdraw two-tone) while keeping row height
          d.cell.styles.textColor = Array.isArray(d.cell.styles.fillColor) ? d.cell.styles.fillColor : WHITE;
        }
        if (d.section === 'body' && d.column.index === 5) {
          var cc = calc[d.row.index];
          if (cc && cc.pct > 0) { d.cell.styles.textColor = SAVE; d.cell.styles.fontStyle = 'bold'; }
          else { d.cell.styles.textColor = MUTED; }
        }
        if (d.section === 'head' && (d.column.index === 4 || d.column.index === 6)) d.cell.styles.halign = 'right';
        if (d.section === 'head' && (d.column.index === 3 || d.column.index === 5)) d.cell.styles.halign = 'center';
      },
      didDrawCell: function (d) {
        if (d.section !== 'body') return;
        var it = items[d.row.index], c = calc[d.row.index];
        if (!it || !c) return;
        var pad = 6;
        if (d.column.index === 0 && it.imageDataUrl) {
          var dim = 30;
          var ix = d.cell.x + (d.cell.width - dim) / 2;
          var iy = d.cell.y + (d.cell.height - dim) / 2;
          var fmt = (String(it.imageDataUrl).indexOf('data:image/png') === 0) ? 'PNG' : 'JPEG';
          try { doc.addImage(it.imageDataUrl, fmt, ix, iy, dim, dim); } catch (e) {}
        }
        if (d.column.index === 1) {
          var name = it.product_title || it.title || '';
          var variant = (it.variant_title && it.variant_title !== 'Default Title') ? it.variant_title : '';
          var tx = d.cell.x + pad, tw = d.cell.width - pad * 2;
          var ty = d.cell.y + 12;
          doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor.apply(doc, DARK);
          doc.splitTextToSize(name, tw).forEach(function (ln) { doc.text(ln, tx, ty); ty += 10.5; });
          if (variant) { doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor.apply(doc, MUTED); doc.text(variant, tx, ty); ty += 9.5; }
          if (c.titles.length) {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor.apply(doc, SAVE);
            doc.splitTextToSize('Sale: ' + c.titles.join('; '), tw).forEach(function (ln) { doc.text(ln, tx, ty); ty += 9; });
          }
        }
        if (d.column.index === 4 && c.origUnit) {
          var rx = d.cell.x + d.cell.width - pad, midY = d.cell.y + d.cell.height / 2;
          doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor.apply(doc, MUTED);
          var os = money(c.origUnit), ow = doc.getTextWidth(os), oy = midY - 4;
          doc.text(os, rx, oy, { align: 'right' });
          doc.setDrawColor.apply(doc, MUTED); doc.setLineWidth(0.5); doc.line(rx - ow, oy - 2.2, rx, oy - 2.2);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor.apply(doc, DARK);
          doc.text(money(c.unitFinal), rx, midY + 8, { align: 'right' });
        }
      }
    });

    var afterY = doc.lastAutoTable.finalY + 16;

    // ===== Totals =====
    var regularTotal = calc.reduce(function (s, c) { return s + c.origLine; }, 0);
    var finalSubtotal = calc.reduce(function (s, c) { return s + c.lineFinal; }, 0);
    var total = (cart.total_price != null) ? cart.total_price : finalSubtotal;
    var youSaved = Math.max(0, regularTotal - total);

    var panelW = 250, panelX = PW - M - panelW, valX = PW - M - 10, labX = panelX + 12;
    var rowsN = youSaved > 0 ? 2 : 0;
    var panelH = 18 + rowsN * 15 + 30;
    if (afterY + panelH > PH - FOOT - 10) { doc.addPage(); afterY = M; }
    doc.setFillColor.apply(doc, PANEL); doc.rect(panelX, afterY, panelW, panelH - 28, 'F');
    var ty = afterY + 16;
    function tline(label, val, opts) {
      opts = opts || {};
      doc.setFont('helvetica', opts.bold ? 'bold' : 'normal'); doc.setFontSize(opts.bold ? 11 : 9.5);
      doc.setTextColor.apply(doc, opts.color || DARK);
      doc.text(label, labX, ty); doc.text(val, valX, ty, { align: 'right' });
      ty += 15;
    }
    if (youSaved > 0) { tline('Regular price', money(regularTotal)); tline('You saved', '-' + money(youSaved), { color: SAVE }); ty += 2; }
    // TOTAL bar (navy)
    doc.setFillColor.apply(doc, NAVY); doc.rect(panelX, ty - 4, panelW, 26, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor.apply(doc, WHITE);
    doc.text('TOTAL (' + (cart.currency || 'CAD') + ')', labX, ty + 13);
    doc.text(money(total), valX, ty + 13, { align: 'right' });
    var afterTotals = ty + 22;
    doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor.apply(doc, MUTED);
    doc.text('Taxes & shipping calculated at checkout.', valX, afterTotals + 11, { align: 'right' });

    // ===== Terms =====
    var termsY = afterTotals + 30;
    var termsBlockH = 22 + TERMS.length * 12 + 4;
    if (termsY + termsBlockH > PH - FOOT - 10) { doc.addPage(); termsY = M; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor.apply(doc, NAVY);
    doc.text('TERMS & CONDITIONS', M, termsY);
    termsY += 14;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor.apply(doc, [80, 80, 80]);
    TERMS.forEach(function (t, i) {
      var lines = doc.splitTextToSize((i + 1) + '. ' + t, PW - 2 * M);
      lines.forEach(function (ln) { doc.text(ln, M, termsY); termsY += 10.5; });
    });

    // ===== Order approval box (sage) =====
    var boxTop = termsY + 14, abH = 92;
    if (boxTop + abH > PH - FOOT - 6) { doc.addPage(); boxTop = M; }
    doc.setFillColor.apply(doc, SAGEBG); doc.setDrawColor.apply(doc, SAGE); doc.setLineWidth(1);
    doc.roundedRect(M, boxTop, PW - 2 * M, abH, 4, 4, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor.apply(doc, NAVY);
    doc.text('ORDER APPROVAL', M + 14, boxTop + 20);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor.apply(doc, DARK);
    doc.text(ACCEPT_TEXT, M + 14, boxTop + 36);
    var sigY = boxTop + 70, w3 = (PW - 2 * M - 28 - 40) / 3;
    var sx = [M + 14, M + 14 + w3 + 20, M + 14 + 2 * (w3 + 20)];
    doc.setDrawColor.apply(doc, [110, 130, 124]); doc.setLineWidth(0.6);
    sx.forEach(function (x) { doc.line(x, sigY, x + w3, sigY); });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor.apply(doc, [90, 100, 96]);
    doc.text('Authorized Signature', sx[0], sigY + 12);
    doc.text('Printed Name', sx[1], sigY + 12);
    doc.text('Date', sx[2], sigY + 12);

    addFooters(doc);
    return doc;
  }

  function addFooters(doc) {
    var pages = doc.internal.getNumberOfPages();
    var PW = doc.internal.pageSize.getWidth();
    var PH = doc.internal.pageSize.getHeight();
    for (var i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFillColor(31, 78, 121); doc.rect(0, PH - 30, PW, 30, 'F');
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
      doc.text('EBIKO Dental   |   437-878-4588   |   ebikodental@gmail.com   |   EBIKO.ca', 36, PH - 11);
      doc.text('Page ' + i + ' of ' + pages, PW - 36, PH - 11, { align: 'right' });
    }
  }

  root.EbikoDraftOrder = { build: buildDraftOrder, money: money, lineCalc: lineCalc, SHOP: SHOP };
  if (typeof module !== 'undefined' && module.exports) { module.exports = root.EbikoDraftOrder; }

  // ==========================================================================
  // Browser-only glue. Skipped under Node (no `document`).
  // ==========================================================================
  if (typeof document === 'undefined') return;

  var JSPDF_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  var AUTOTABLE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
  var libsReady = null;

  function loadScript(src) {
    return new Promise(function (res, rej) {
      var s = document.createElement('script'); s.src = src; s.async = true;
      s.onload = function () { res(); };
      s.onerror = function () { rej(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }
  function ensureLibs() {
    if (libsReady) return libsReady;
    libsReady = (function () {
      var p = Promise.resolve();
      if (!(root.jspdf && root.jspdf.jsPDF)) p = p.then(function () { return loadScript(JSPDF_URL); });
      p = p.then(function () { return loadScript(AUTOTABLE_URL); });
      return p;
    })();
    return libsReady;
  }
  function loadThumb(url) {
    return new Promise(function (res) {
      if (!url) return res(null);
      var src = url;
      try { src = url.replace(/(\.(?:png|jpe?g|webp))(\?.*)?$/i, '_120x120$1$2'); } catch (e) {}
      var img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var s = 96, c = document.createElement('canvas'); c.width = s; c.height = s;
          var ctx = c.getContext('2d'); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, s, s);
          var ratio = Math.min(s / img.width, s / img.height), w = img.width * ratio, h = img.height * ratio;
          ctx.drawImage(img, (s - w) / 2, (s - h) / 2, w, h);
          res(c.toDataURL('image/jpeg', 0.85));
        } catch (e) { res(null); }
      };
      img.onerror = function () { res(null); };
      img.src = src;
    });
  }
  function loadLogo(url) {
    return new Promise(function (res) {
      if (!url) return res(null);
      var img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var maxW = 320, scale = Math.min(1, maxW / img.width), w = Math.round(img.width * scale), h = Math.round(img.height * scale);
          var c = document.createElement('canvas'); c.width = w; c.height = h; c.getContext('2d').drawImage(img, 0, 0, w, h);
          res({ dataUrl: c.toDataURL('image/png'), ratio: img.width / img.height });
        } catch (e) { res(null); }
      };
      img.onerror = function () { res(null); };
      img.src = url;
    });
  }
  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent || el.innerText || 'null'); } catch (e) { return null; }
  }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function setBusy(btn, busy, label) {
    if (!btn) return;
    if (busy) { btn.dataset.ebkLabel = btn.textContent; btn.textContent = label || 'Working…'; btn.setAttribute('disabled', 'disabled'); btn.style.opacity = '0.7'; }
    else { if (btn.dataset.ebkLabel) btn.textContent = btn.dataset.ebkLabel; btn.removeAttribute('disabled'); btn.style.opacity = ''; }
  }

  var TRACKER = 'https://ebiko-order-tracker.onrender.com';

  function toast(msg) {
    var t = document.createElement('div');
    t.textContent = msg;
    t.style.cssText = 'position:fixed;left:50%;bottom:28px;transform:translateX(-50%);background:#1F4E79;color:#fff;padding:12px 18px;border-radius:8px;font:14px/1.4 -apple-system,Helvetica,Arial,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.25);z-index:99999;max-width:90vw;text-align:center;opacity:0;transition:opacity .2s;';
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.style.opacity = '1'; });
    setTimeout(function () { t.style.opacity = '0'; setTimeout(function () { t.remove(); }, 300); }, 3800);
  }

  function browserDefaultLabel() {
    var d = new Date();
    var m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return 'Order — ' + m[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
  }

  // Save the current cart to the logged-in account via the tracker API.
  function saveOrder(btn) {
    var customer = readJSON('ebk-customer-data');
    if (!customer || !customer.id) { alert('Please sign in to save an order to your account.'); return; }
    var cart;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        cart = c;
        if (!cart.items || !cart.items.length) throw new Error('EMPTY');
        var label = window.prompt('Name this saved order (so your team can find it):', browserDefaultLabel());
        if (label === null) throw new Error('CANCELLED');
        var compareMap = readJSON('ebk-compare-at') || {};
        var items = cart.items.map(function (it) {
          var ca = compareMap[String(it.variant_id != null ? it.variant_id : it.id)] || 0;
          return {
            variant_id: it.variant_id, product_id: it.product_id,
            product_title: it.product_title || it.title || '', variant_title: it.variant_title || '', sku: it.sku || '',
            quantity: it.quantity, final_price: it.final_price, original_price: it.original_price,
            final_line_price: it.final_line_price, original_line_price: it.original_line_price,
            compare_at_price: ca, image: it.image || (it.featured_image && it.featured_image.url) || '',
            discounts: it.discounts || [], url: it.url || ''
          };
        });
        var payload = {
          customer_id: customer.id, customer_email: customer.email || '', saved_by: customer.name || '',
          label: (label || '').trim(),
          cart: { currency: cart.currency, items_subtotal_price: cart.items_subtotal_price, total_price: cart.total_price, total_discount: cart.total_discount, items: items }
        };
        setBusy(btn, true, 'Saving…');
        return fetch(TRACKER + '/api/storefront/saved-orders/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).then(function (r) { return r.json(); });
      })
      .then(function (res) {
        if (res && res.success) toast('✓ Saved — find it under My account → Saved Orders');
        else if (res) alert(res.error || 'Could not save the order.');
      })
      .catch(function (err) {
        if (err && err.message === 'EMPTY') alert('Your cart is empty — add items first.');
        else if (err && err.message === 'CANCELLED') { /* user cancelled the name prompt */ }
        else { console.error('[EBIKO Save Order]', err); alert('Could not save the order. Please try again.'); }
      })
      .then(function () { setBusy(btn, false); });
  }

  function generate(btn) {
    setBusy(btn, true, 'Generating PDF…');
    var cart;
    var logoUrl = (btn && btn.dataset && btn.dataset.logoUrl) || null;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        cart = c;
        if (!cart.items || !cart.items.length) throw new Error('EMPTY');
        var jobs = cart.items.map(function (it) {
          var u = it.image || (it.featured_image && it.featured_image.url) || null;
          return loadThumb(u).then(function (d) { it.imageDataUrl = d; });
        });
        jobs.push(loadLogo(logoUrl).then(function (l) { cart.__logo = l; }));
        return Promise.all(jobs);
      })
      .then(function () { return ensureLibs(); })
      .then(function () {
        var JsPDF = root.jspdf.jsPDF;
        var doc = new JsPDF({ unit: 'pt', format: 'letter' });
        var now = new Date();
        var ymd = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
        var dateStr = now.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
        var docNumber = 'DO-' + now.getFullYear() + pad(now.getMonth() + 1) + pad(now.getDate()) + '-' + pad(now.getHours()) + pad(now.getMinutes());
        var customer = readJSON('ebk-customer-data');
        var compareMap = readJSON('ebk-compare-at') || {};
        root.EbikoDraftOrder.build(doc, { cart: cart, items: cart.items, customer: customer, compareMap: compareMap, logo: cart.__logo, dateStr: dateStr, docNumber: docNumber });
        var clinic = (customer && customer.company) ? ('-' + String(customer.company).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')) : '';
        doc.save('EBIKO-Draft-Order' + clinic + '-' + ymd + '.pdf');
      })
      .catch(function (err) {
        if (err && err.message === 'EMPTY') alert('Your cart is empty — add some items first.');
        else { console.error('[EBIKO Draft Order]', err); alert('Sorry, the PDF could not be generated. Please try again.'); }
      })
      .then(function () { setBusy(btn, false); });
  }

  // Public: build a Draft Order PDF from a saved-order snapshot (used by the account dashboard).
  function downloadFromSaved(saved, customer, logoUrl) {
    saved = saved || {};
    var items = (saved.items || []).map(function (it) { var c = {}; for (var k in it) c[k] = it[k]; return c; });
    if (!items.length) { alert('This saved order has no items.'); return Promise.resolve(); }
    var compareMap = {};
    items.forEach(function (it) { if (it.compare_at_price) compareMap[String(it.variant_id)] = it.compare_at_price; });
    return ensureLibs().then(function () {
      var jobs = items.map(function (it) { return loadThumb(it.image).then(function (d) { it.imageDataUrl = d; }); });
      var holder = {};
      jobs.push(loadLogo(logoUrl).then(function (l) { holder.logo = l; }));
      return Promise.all(jobs).then(function () {
        var JsPDF = root.jspdf.jsPDF;
        var doc = new JsPDF({ unit: 'pt', format: 'letter' });
        var now = new Date();
        var dateStr = now.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
        var docNumber = 'DO-' + now.getFullYear() + pad(now.getMonth() + 1) + pad(now.getDate()) + '-' + pad(now.getHours()) + pad(now.getMinutes());
        var cart = { currency: saved.currency || 'CAD', items_subtotal_price: saved.subtotal_cents, total_price: saved.total_cents, items: items };
        buildDraftOrder(doc, { cart: cart, items: items, customer: customer || null, compareMap: compareMap, logo: holder.logo, dateStr: dateStr, docNumber: docNumber });
        var clean = function (s) { return String(s || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, ''); };
        doc.save('EBIKO-Draft-Order' + (saved.label ? '-' + clean(saved.label).slice(0, 40) : '') + '.pdf');
      });
    }).catch(function (err) { console.error('[EBIKO Draft Order] saved PDF', err); alert('Could not generate the PDF.'); });
  }
  root.EbikoDraftOrder.downloadFromSaved = downloadFromSaved;

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var dl = e.target.closest('#ebk-download-order-pdf');
    if (dl) { e.preventDefault(); generate(dl); return; }
    var sv = e.target.closest('#ebk-save-order');
    if (sv) { e.preventDefault(); saveOrder(sv); return; }
  });
})(typeof window !== 'undefined' ? window : globalThis);
